import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  ViewChild,
  effect,
  inject,
  signal,
  computed,
  DestroyRef,
  ElementRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular';
import type { CalendarOptions, DatesSetArg, DayCellMountArg, EventInput } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, { type DateClickArg } from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';
import { VacacionesService } from '@services/vacaciones.service';
import { FestivosService } from '@services/festivos.service';
import { VacacionesCalendario, EstatusVacaciones } from '@schemas/vacaciones.schema';
import { inicioDelDia, parseLocalDate, toISODateString } from '@shared/vacaciones.utils';
import { getEstatusColor as _getEstatusColor } from '@shared/vacaciones.helpers';

import { NzTooltipModule } from 'ng-zorro-antd/tooltip';

import { EmptyState, PageHeader } from '@intelishs-group/ui';

@Component({
  selector: 'app-calendario',
  imports: [
    NzTooltipModule,
    FormsModule,
    FullCalendarModule,
    NzSelectModule,
    NzButtonModule,
    NzIconModule,
    NzTagModule,
    PageHeader,
    EmptyState,
  ],
  templateUrl: './calendario.page.html',
  styleUrl: './calendario.page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarioPage implements OnInit {
  private readonly vacacionesService = inject(VacacionesService);
  readonly festivosService = inject(FestivosService);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild(FullCalendarComponent) private fc?: FullCalendarComponent;
  @ViewChild('panelDia') private panelDia?: ElementRef<HTMLElement>;

  eventos = signal<VacacionesCalendario[]>([]);
  areaFilter = signal('');
  /** ID del empleado filtrado, o null si no hay filtro de empleado. */
  empleadoFilterId = signal<number | null>(null);
  /** Nombre del empleado filtrado — sólo para mostrar en el chip. */
  empleadoFilterNombre = signal<string>('');
  /**
   * Arranca en HOY. El panel nacía en `null`, así que medio ancho de pantalla
   * decía "Selecciona un día" aunque hoy tuviera ausencias —y el calendario ya
   * marca hoy con su círculo—. Preseleccionar sirve incluso cuando hoy no tiene
   * ninguna: "Sin ausencias este día" responde "¿quién falta hoy?", que es la
   * primera pregunta de un calendario de ausencias; el panel vacío no responde
   * nada.
   *
   * A medianoche y no `new Date()` pelado: `selectedDayEvents` compara contra
   * fechas parseadas a las 00:00, así que con la hora actual dentro una ausencia
   * que TERMINA hoy quedaría fuera del rango (`sel <= end` sería falso).
   */
  selectedDate = signal<Date | null>(inicioDelDia(new Date()));

  /**
   * Estatus visibles en el calendario. Los cuatro empiezan encendidos, así que
   * el estado inicial es el de antes: nada filtrado. Apagar uno esconde sus
   * eventos —semántica de leyenda de gráfica, que permite combinar (ver solo
   * pendientes y aprobadas por director) en vez de elegir una sola—.
   *
   * Antes eran `<span>` sin acción, dibujados igual que los chips de filtro del
   * resto de la app: prometían un filtro por estatus que no existía.
   */
  private readonly estatusOcultos = signal<ReadonlySet<string>>(new Set());
  loading = signal(false);

  areas = computed(() => {
    const unique = new Set(
      this.eventos()
        .map((e) => e.area)
        .filter(Boolean),
    );
    return Array.from(unique) as string[];
  });

  /** True si hay cualquier filtro activo (área, empleado o estatus apagado). */
  hayFiltroActivo = computed(
    () =>
      this.areaFilter() !== '' ||
      this.empleadoFilterId() !== null ||
      this.estatusOcultos().size > 0,
  );

  selectedDayEvents = computed(() => {
    const sel = this.selectedDate();
    if (!sel) return [];
    return this.getFilteredEvents().filter((e) => {
      const start = parseLocalDate(e.fechaInicio);
      const end = parseLocalDate(e.fechaFin);
      return sel >= start && sel <= end;
    });
  });

  selectedDayHoliday = computed(() => {
    const sel = this.selectedDate();
    if (!sel) return undefined;
    return this.festivosService.getNombre(sel);
  });

  calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, interactionPlugin],
    initialView: 'dayGridMonth',
    locale: esLocale,
    firstDay: 1,
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: '',
    },
    buttonText: { today: 'Hoy' },
    // "Agosto 2026" y no el "agosto de 2026" del locale es: la preposición no
    // aporta y el mes en minúscula pesa poco para ser el título de la vista.
    // Se arma con el mismo array `MESES` que usa el panel lateral, así que los
    // dos encabezados de la página nombran el mes igual.
    titleFormat: (info) => `${this.MESES[info.date.month]} ${info.date.year}`,
    height: 'auto',
    dayMaxEventRows: 2,
    moreLinkText: (n: number) => `+${n} más`,
    // «+N más» abre el día en el panel lateral, que ya lista a todos con su
    // área y periodo, en columnas y con scroll propio. El popover de
    // FullCalendar repetía sólo los nombres y lo recortaba el borde
    // redondeado del calendario (`overflow: hidden` de `.calendar-wrap`).
    //
    // FullCalendar no tiene un «no hagas nada»: si el handler no devuelve una
    // vista abre el popover, y devolver la vista actual navegaría al mes vecino
    // al pulsar el «+N más» de un día gris. Por eso el handler no devuelve nada
    // y el popover se oculta por CSS (`.fc-more-popover` en la hoja).
    moreLinkClick: (arg) => {
      this.verDiaEnPanel(arg.date);
    },
    weekends: true,
    fixedWeekCount: false,
    events: [],
    eventDisplay: 'block',
    dateClick: (arg: DateClickArg) => this.verDiaEnPanel(arg.date),
    eventClick: (arg) => {
      // Solo reaccionar a eventos de vacaciones (no a festivos de fondo).
      const props = arg.event.extendedProps as { empleadoId?: number; empleadoNombre?: string };
      if (typeof props.empleadoId === 'number' && props.empleadoNombre) {
        arg.jsEvent.preventDefault();
        arg.jsEvent.stopPropagation();
        this.filtrarPorEmpleado(props.empleadoId, props.empleadoNombre);
      }
    },
    dayCellDidMount: (arg: DayCellMountArg) => {
      const nombre = this.festivosService.getNombre(arg.date);
      if (nombre) {
        arg.el.classList.add('fc-day-holiday');
        arg.el.setAttribute('title', nombre);
      }
    },
    // Cuando el usuario navega (prev/next/today o cambia de vista),
    // FullCalendar dispara este callback con el rango visible. Lo usamos
    // para fetch on-demand: si el rango se sale de lo ya cargado,
    // expandimos hacia atrás o hacia adelante.
    datesSet: (arg: DatesSetArg) => this.onDatesSet(arg),
  };

  constructor() {
    // Sincroniza eventos (vacaciones + festivos) con el calendario de forma reactiva.
    effect(() => {
      const vacaciones = this.getFilteredEvents();
      const festivos = this.festivosService.festivos();
      const api = this.fc?.getApi();
      if (!api) return;

      // Set de festivos para descartarlos al expandir el rango por día.
      const festivosSet = new Set(festivos.map((f) => f.fecha));

      const fcEvents: EventInput[] = [
        ...vacaciones.flatMap((v) => this.toFcEvents(v, festivosSet)),
        ...(this.esVisible('festivo') ? festivos : []).map((f) => ({
          id: `festivo-${f.fecha}`,
          start: f.fecha,
          allDay: true,
          display: 'background' as const,
          backgroundColor: 'rgba(250, 173, 20, 0.18)',
          extendedProps: { tipo: 'festivo', nombre: f.nombre },
        })),
      ];

      api.removeAllEvents();
      fcEvents.forEach((ev) => api.addEvent(ev));
      // Fuerza repintado de clases de festivo si los festivos cambiaron después del mount.
      api.render();
    });

    // Marco azul en la celda del día seleccionado. FullCalendar no expone
    // esto nativamente, así que sincronizamos el atributo `data-date` con
    // un toggle de clase CSS via DOM. Es liviano (un querySelector + dos
    // classList.toggle) y reactivo a `selectedDate()`.
    effect(() => {
      const sel = this.selectedDate();
      const api = this.fc?.getApi();
      if (!api) return;
      // Quita cualquier marca anterior.
      const root =
        (api as unknown as { el?: HTMLElement }).el ?? document.querySelector('.calendar-wrap');
      if (!root) return;
      root
        .querySelectorAll<HTMLElement>('.fc-day-selected')
        .forEach((el) => el.classList.remove('fc-day-selected'));
      if (!sel) return;
      // FullCalendar marca cada celda con `data-date="YYYY-MM-DD"`.
      const dayStr = toISODateString(sel);
      const cell = root.querySelector<HTMLElement>(`.fc-daygrid-day[data-date="${dayStr}"]`);
      cell?.classList.add('fc-day-selected');
    });
  }

  /**
   * Rango ya cargado. Usado por <see cref="onDatesSet"/> para decidir si
   * hace falta hacer fetch al navegar a un mes anterior/posterior.
   */
  private rangoCargadoDesde?: Date;
  private rangoCargadoHasta?: Date;

  ngOnInit(): void {
    const anioActual = new Date().getFullYear();
    this.festivosService.cargar([anioActual, anioActual + 1]);
    // El primer fetch del calendario lo dispara `datesSet` automáticamente
    // cuando FullCalendar termina su mount inicial — no llamamos cargar()
    // aquí para evitar un doble fetch.
  }

  /**
   * Hook de navegación de FullCalendar. Recibe el rango de fechas que
   * está actualmente visible (incluye días de meses adyacentes que
   * aparecen en la grilla). Si ese rango se sale del que ya tenemos
   * cargado, hacemos un fetch que cubre lo nuevo + un buffer para
   * minimizar el ping-pong al navegar mes a mes.
   */
  private onDatesSet(arg: DatesSetArg): void {
    const visibleStart = arg.start;
    const visibleEnd = arg.end;
    const yaCargado =
      this.rangoCargadoDesde &&
      this.rangoCargadoHasta &&
      visibleStart >= this.rangoCargadoDesde &&
      visibleEnd <= this.rangoCargadoHasta;
    if (yaCargado) return;

    // Buffer de ±3 meses para amortiguar la navegación.
    const desde = new Date(visibleStart);
    desde.setMonth(desde.getMonth() - 3);
    const hasta = new Date(visibleEnd);
    hasta.setMonth(hasta.getMonth() + 3);

    // Si ya teníamos algo cargado, expandimos hacia el lado faltante en
    // lugar de reemplazar — así un usuario que navega siempre hacia atrás
    // no pierde lo que ya vio cargar.
    if (this.rangoCargadoDesde && desde > this.rangoCargadoDesde)
      desde.setTime(this.rangoCargadoDesde.getTime());
    if (this.rangoCargadoHasta && hasta < this.rangoCargadoHasta)
      hasta.setTime(this.rangoCargadoHasta.getTime());

    this.cargar(desde, hasta);
  }

  cargar(desde?: Date, hasta?: Date): void {
    // Si no se pasan fechas, valor por defecto: ±6 meses alrededor de hoy.
    if (!desde || !hasta) {
      const hoy = new Date();
      desde = new Date(hoy.getFullYear(), hoy.getMonth() - 6, 1);
      hasta = new Date(hoy.getFullYear(), hoy.getMonth() + 6, 0);
    }
    // Captura locales para que las usen los handlers (TypeScript ya
    // inferiría su no-nulidad aquí, pero lo dejamos explícito).
    const desdeLocal = desde;
    const hastaLocal = hasta;

    this.loading.set(true);
    this.vacacionesService
      .getCalendario(desdeLocal, hastaLocal)
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (data) => {
          this.eventos.set(data);
          this.rangoCargadoDesde = desdeLocal;
          this.rangoCargadoHasta = hastaLocal;
        },
      });
  }

  /**
   * Clase CSS por estatus para pintar el evento del calendario con el
   * mismo estilo de pill que la columna "Estatus" en la tabla de
   * Vacaciones (fondo claro, borde y texto del color, vía nz-tag).
   * Definidas en `calendario.page.css`.
   */
  getEventEstatusClass(estatus: EstatusVacaciones): string {
    if (estatus === 'Aprobada') return 'sinay-vacacion--aprobada';
    if (estatus === 'Aprobada Director') return 'sinay-vacacion--aprobada-dir';
    return 'sinay-vacacion--pendiente';
  }

  /**
   * Color hex por estatus. Lo usa el panel lateral derecho para el
   * `border-left-color` de cada tarjeta de detalle. NO se usa para los
   * eventos del calendario — esos toman el tinte completo desde
   * `getEventEstatusClass`.
   */
  getEventColor(estatus: EstatusVacaciones): string {
    if (estatus === 'Aprobada') return '#52c41a';
    if (estatus === 'Aprobada Director') return '#1890ff';
    return '#fa8c16';
  }

  getEstatusColor = _getEstatusColor;

  /**
   * Las cuatro entradas de la leyenda. Como datos y no como markup repetido,
   * para que agregar un estatus no signifique copiar y pegar un bloque más.
   */
  readonly leyenda = [
    { clave: 'Aprobada', etiqueta: 'Aprobada' },
    { clave: 'Aprobada Director', etiqueta: 'Aprobada Director' },
    { clave: 'Pendiente', etiqueta: 'Pendiente' },
    { clave: 'festivo', etiqueta: 'Día festivo' },
  ] as const;

  /** Clave `festivo` incluida: apagarla esconde el fondo ámbar de los feriados. */
  esVisible(clave: string): boolean {
    return !this.estatusOcultos().has(clave);
  }

  toggleEstatus(clave: string): void {
    const copia = new Set(this.estatusOcultos());
    if (copia.has(clave)) copia.delete(clave);
    else copia.add(clave);
    this.estatusOcultos.set(copia);
  }

  /** Color de la leyenda: el MISMO que pinta el cuadrito en la rejilla. */
  colorLeyenda(clave: string): string {
    return clave === 'festivo' ? '#faad14' : this.getEventColor(clave as EstatusVacaciones);
  }

  private readonly MESES = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ];
  private readonly MESES_CORTOS = [
    'Ene',
    'Feb',
    'Mar',
    'Abr',
    'May',
    'Jun',
    'Jul',
    'Ago',
    'Sep',
    'Oct',
    'Nov',
    'Dic',
  ];
  private readonly DIAS_SEMANA_LARGO = [
    'Domingo',
    'Lunes',
    'Martes',
    'Miércoles',
    'Jueves',
    'Viernes',
    'Sábado',
  ];

  /** '08 Abril, 2026' */
  formatSelectedDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    return `${day} ${this.MESES[date.getMonth()]}, ${date.getFullYear()}`;
  }

  /** 'Miércoles' */
  formatSelectedWeekday(date: Date): string {
    return this.DIAS_SEMANA_LARGO[date.getDay()];
  }

  /** '03 Abr — 10 Abr' (o '03 Abr' si inicio y fin coinciden) */
  /**
   * Selecciona el día y, con la página apilada (≤768px, el panel va DEBAJO del
   * calendario), baja hasta el panel: si no, el clic no tendría efecto visible.
   * El corte es el mismo `min-width: 769px` del CSS de la página.
   */
  private verDiaEnPanel(fecha: Date): void {
    this.selectedDate.set(fecha);
    if (window.matchMedia('(max-width: 768px)').matches) {
      this.panelDia?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  formatEventRange(fechaInicio: string, fechaFin: string): string {
    const start = parseLocalDate(fechaInicio);
    const end = parseLocalDate(fechaFin);
    const fmt = (d: Date) =>
      `${String(d.getDate()).padStart(2, '0')} ${this.MESES_CORTOS[d.getMonth()]}`;
    return start.getTime() === end.getTime() ? fmt(start) : `${fmt(start)} — ${fmt(end)}`;
  }

  /**
   * Devuelve UN evento de FullCalendar por cada día hábil del rango de la
   * solicitud. Los sábados, domingos y festivos se omiten — el chip no se
   * pinta sobre celdas que no son días laborables (los festivos siguen
   * resaltados con su propio fondo amarillo de fondo).
   *
   * Antes pintábamos un solo evento con start/end como rango continuo, lo
   * que producía una barra horizontal de varios días. Para tener "un
   * cuadrito por día" expandimos al nivel de día con allDay.
   */
  private toFcEvents(v: VacacionesCalendario, festivosSet: Set<string>): EventInput[] {
    const estatusClass = this.getEventEstatusClass(v.estatus);
    const inicio = parseLocalDate(v.fechaInicio);
    const fin = parseLocalDate(v.fechaFin);

    const events: EventInput[] = [];
    const cursor = new Date(inicio);
    while (cursor.getTime() <= fin.getTime()) {
      const dow = cursor.getDay(); // 0=domingo, 6=sábado
      const dayStr = toISODateString(cursor);
      const esLaborable = dow !== 0 && dow !== 6 && !festivosSet.has(dayStr);

      if (esLaborable) {
        events.push({
          id: `${v.empleadoId}-${dayStr}`,
          // Nombre completo, truncado por CSS. Con solo el nombre de pila, dos
          // homónimos ausentes el mismo día eran indistinguibles en la rejilla,
          // y la celda tiene ancho de sobra.
          title: v.empleadoNombre,
          start: dayStr,
          allDay: true,
          // Sin colores inline: la clase aplicada en CSS define fondo,
          // borde y texto con el mismo tinte que la pill de la tabla.
          classNames: ['sinay-vacacion', estatusClass],
          display: 'block',
          extendedProps: {
            empleadoId: v.empleadoId,
            empleadoNombre: v.empleadoNombre,
            area: v.area,
            dias: v.dias,
            estatus: v.estatus,
            // fechaInicio/fechaFin del grupo lógico se conservan en los
            // extended props por si el detalle del día las necesita para
            // formatear el rango original ("03 May — 09 May").
            fechaInicioRango: v.fechaInicio,
            fechaFinRango: v.fechaFin,
          },
        });
      }

      cursor.setDate(cursor.getDate() + 1);
    }
    return events;
  }

  private getFilteredEvents(): VacacionesCalendario[] {
    const area = this.areaFilter();
    const empleadoId = this.empleadoFilterId();
    const ocultos = this.estatusOcultos();
    let filtered = this.eventos();
    if (area) filtered = filtered.filter((e) => e.area === area);
    if (empleadoId !== null) filtered = filtered.filter((e) => e.empleadoId === empleadoId);
    if (ocultos.size > 0) filtered = filtered.filter((e) => !ocultos.has(e.estatus));
    return filtered;
  }

  /**
   * Filtrar el calendario por un empleado específico. Se invoca tanto desde
   * el chip del calendario (eventClick) como desde el panel lateral.
   * Identificamos por empleadoId (no por nombre) para evitar ambigüedad
   * cuando hay homónimos.
   */
  filtrarPorEmpleado(empleadoId: number, nombre: string): void {
    this.empleadoFilterId.set(empleadoId);
    this.empleadoFilterNombre.set(nombre);
  }

  /** Quita solo el filtro de empleado (deja el de área intacto). */
  limpiarFiltroEmpleado(): void {
    this.empleadoFilterId.set(null);
    this.empleadoFilterNombre.set('');
  }

  /** Quita todos los filtros (área + empleado). */
  /** Deja la vista como al entrar: sin área, sin empleado y los cuatro estatus visibles. */
  limpiarTodosLosFiltros(): void {
    this.areaFilter.set('');
    this.limpiarFiltroEmpleado();
    this.estatusOcultos.set(new Set());
  }
}
